/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {DOCUMENT, ɵparseCookieValue as parseCookieValue} from '@angular/common';
import {Inject, Injectable, InjectionToken, PLATFORM_ID} from '@angular/core';
import {Observable} from 'rxjs/Observable';

import {HttpHandler} from './backend';
import {HttpInterceptor} from './interceptor';
import {HttpRequest} from './request';
import {HttpEvent} from './response';

export const /** @type {?} */ XSRF_COOKIE_NAME = new InjectionToken<string>('XSRF_COOKIE_NAME');
export const /** @type {?} */ XSRF_HEADER_NAME = new InjectionToken<string>('XSRF_HEADER_NAME');
/**
 * Retrieves the current XSRF token to use with the next outgoing request.
 * 
 * \@experimental
 * @abstract
 */
export abstract class HttpXsrfTokenExtractor {
/**
 * Get the XSRF token to use with an outgoing request.
 * 
 * Will be called for every request, so the token may change between requests.
 * @abstract
 * @return {?}
 */
getToken() {}
}
/**
 * `HttpXsrfTokenExtractor` which retrieves the token from a cookie.
 */
export class HttpXsrfCookieExtractor implements HttpXsrfTokenExtractor {
private lastCookieString: string = '';
private lastToken: string|null = null;
/**
 * \@internal for testing
 */
parseCount: number = 0;
/**
 * @param {?} doc
 * @param {?} platform
 * @param {?} cookieName
 */
constructor(
private doc: any,
private platform: string,
private cookieName: string) {}
/**
 * @return {?}
 */
getToken(): string|null {
    if (this.platform === 'server') {
      return null;
    }
    const /** @type {?} */ cookieString = this.doc.cookie || '';
    if (cookieString !== this.lastCookieString) {
      this.parseCount++;
      this.lastToken = parseCookieValue(cookieString, this.cookieName);
      this.lastCookieString = cookieString;
    }
    return this.lastToken;
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: undefined, decorators: [{ type: Inject, args: [DOCUMENT, ] }, ]},
{type: undefined, decorators: [{ type: Inject, args: [PLATFORM_ID, ] }, ]},
{type: undefined, decorators: [{ type: Inject, args: [XSRF_COOKIE_NAME, ] }, ]},
];
}

function HttpXsrfCookieExtractor_tsickle_Closure_declarations() {
/** @type {?} */
HttpXsrfCookieExtractor.decorators;
/**
 * @nocollapse
 * @type {?}
 */
HttpXsrfCookieExtractor.ctorParameters;
/** @type {?} */
HttpXsrfCookieExtractor.prototype.lastCookieString;
/** @type {?} */
HttpXsrfCookieExtractor.prototype.lastToken;
/**
 * \@internal for testing
 * @type {?}
 */
HttpXsrfCookieExtractor.prototype.parseCount;
/** @type {?} */
HttpXsrfCookieExtractor.prototype.doc;
/** @type {?} */
HttpXsrfCookieExtractor.prototype.platform;
/** @type {?} */
HttpXsrfCookieExtractor.prototype.cookieName;
}

/**
 * `HttpInterceptor` which adds an XSRF token to eligible outgoing requests.
 */
export class HttpXsrfInterceptor implements HttpInterceptor {
/**
 * @param {?} tokenService
 * @param {?} headerName
 */
constructor(
private tokenService: HttpXsrfTokenExtractor,
private headerName: string) {}
/**
 * @param {?} req
 * @param {?} next
 * @return {?}
 */
intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const /** @type {?} */ lcUrl = req.url.toLowerCase();
    // Skip both non-mutating requests and absolute URLs.
    // Non-mutating requests don't require a token, and absolute URLs require special handling
    // anyway as the cookie set
    // on our origin is not the same as the token expected by another origin.
    if (req.method === 'GET' || req.method === 'HEAD' || lcUrl.startsWith('http://') ||
        lcUrl.startsWith('https://')) {
      return next.handle(req);
    }
    const /** @type {?} */ token = this.tokenService.getToken();

    // Be careful not to overwrite an existing header of the same name.
    if (token !== null && !req.headers.has(this.headerName)) {
      req = req.clone({headers: req.headers.set(this.headerName, token)});
    }
    return next.handle(req);
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: HttpXsrfTokenExtractor, },
{type: undefined, decorators: [{ type: Inject, args: [XSRF_HEADER_NAME, ] }, ]},
];
}

function HttpXsrfInterceptor_tsickle_Closure_declarations() {
/** @type {?} */
HttpXsrfInterceptor.decorators;
/**
 * @nocollapse
 * @type {?}
 */
HttpXsrfInterceptor.ctorParameters;
/** @type {?} */
HttpXsrfInterceptor.prototype.tokenService;
/** @type {?} */
HttpXsrfInterceptor.prototype.headerName;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}



// WEBPACK FOOTER //
// ../../../../../packages/common/http/src/xsrf.ts